// An ad copy as live's editor holds it (`edit-text-copy-form/schema.ts`): its title, and its
// sections in live's order, each with its values; what a save sends (G8's `PATCH`).
import type { AdCopy, AdCopyElementsBody } from "../../../api/generated/model";
import { SECTIONS, valuesOf, type TextKey } from "./elements";

export interface EditSection {
  key: TextKey;
  values: string[];
}

export interface CopyDraft {
  name: string;
  sections: EditSection[];
}

/** The copy's title and the sections it has values in (live's `getTextCopiesInitialData`). */
export function draftOf(copy: AdCopy): CopyDraft {
  return {
    name: copy.name,
    sections: SECTIONS.map(({ key }) => ({
      key,
      values: valuesOf(copy.text_elements, key),
    })).filter((section) => section.values.length > 0),
  };
}

/** Live's rule: a title, a section, and no section or value empty. */
export function draftValid(draft: CopyDraft): boolean {
  return (
    draft.name.trim() !== "" &&
    draft.sections.length > 0 &&
    draft.sections.every(
      (section) =>
        section.values.length > 0 && section.values.every((value) => value.trim() !== ""),
    )
  );
}

/** Whether the draft differs from what was saved (live's `isDirty`). */
export function draftChanged(draft: CopyDraft, saved: CopyDraft): boolean {
  return JSON.stringify(draft) !== JSON.stringify(saved);
}

/**
 * What a save sends: every section, its values or null, as live's `transformFormDataToRequest`
 * sends them, so a section removed is cleared (G8: null clears one, one left out stays). The
 * bullets' mode is left out, so the copy keeps it: live resets it to "standard" on every edit
 * (`pages/images/utils.ts:58`), which turns a comparison copy standard (spec G8-advanced-mode
 * difference 6).
 */
export function elementsOf(draft: CopyDraft): AdCopyElementsBody {
  const body: AdCopyElementsBody = {};
  for (const { key } of SECTIONS) {
    body[key] = draft.sections.find((section) => section.key === key)?.values ?? null;
  }
  return body;
}
